import type { Action } from "~/modules/actions/Action";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import {
    canHold,
    checkParticipantExists,
    checkReservationExists,
    getReservationsByTask,
    handleGetParticipantsByTask
} from "~/modules/actions/ActionUtils";
import { sendWorkerTrackingEvent, toCauseMessage } from "~/modules/telemetry/utils";
import { getLogger, Logger, LoggerName } from "~/modules/logger";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { CbmImpl } from "~/modules/cbm";
import { TaskParticipant } from "~/modules/participant";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { throwFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";
import { Reservation, Supervisor } from "~/packages/taskrouter";

interface HoldVoiceParticipantContext {
    context: ContextManager;
    taskRouter: TaskRouterImpl;
    logger: Logger;
    analytics: AnalyticsImpl;
    cbm: CbmImpl;
    worker: Supervisor;
    holdMusicUrl: string | undefined;
    holdMusicMethod: string;
}





export interface HoldVoiceParticipantOptions {
    


    holdMusicUrl?: string;
    


    holdMusicMethod?: string;
}





































export class HoldVoiceParticipant implements Action<Promise<void>> {
    


    readonly #targetParticipantSid: string;

    


    readonly #taskSid: string;

    


    readonly #options?: HoldVoiceParticipantOptions;

    constructor(targetParticipantSid: string, taskSid: string, options?: HoldVoiceParticipantOptions) {
        this.#targetParticipantSid = targetParticipantSid;
        this.#taskSid = taskSid;
        this.#options = options;
    }

    async run(ctx: {}): Promise<void> {
        const context = ctx as ContextManager;
        const holdContext = await this.#initializeContext(context);
        const reservation = this.#validateReservation(holdContext);
        const participant = await this.#validateParticipant(holdContext, reservation);
        await this.#executeHold(holdContext, reservation, participant);
    }

    async #initializeContext(context: ContextManager): Promise<HoldVoiceParticipantContext> {
        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const logger = getLogger(context)(LoggerName.Client);
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        const worker = taskRouter.worker;
        const holdMusicUrl = this.#options?.holdMusicUrl;
        const holdMusicMethod = this.#options?.holdMusicMethod ?? "GET";

        return {
            context,
            taskRouter,
            logger,
            analytics,
            cbm,
            worker,
            holdMusicUrl,
            holdMusicMethod
        };
    }

    #validateReservation(holdContext: HoldVoiceParticipantContext): Reservation {
        const { worker, logger, analytics, context } = holdContext;

        const reservations = getReservationsByTask(worker, this.#taskSid);
        const reservation = reservations.find((res) => res.status === "accepted");

        checkReservationExists(reservation, this.#taskSid, {
            actionName: "HoldVoiceParticipant",
            moduleName: "actions/Voice",
            logger,
            analytics,
            context,
            event: EVENTS.HoldVoiceParticipantCompleted,
            extra: {
                taskSid: this.#taskSid,
                reservationSid: reservation?.sid,
                targetParticipantSid: this.#targetParticipantSid
            },
            worker
        });

        const validReservation = reservation!;

        if (!canHold(validReservation, worker)) {
            const errorMsg =
                `HoldVoiceParticipant: Cannot hold reservation ${validReservation.sid}` +
                ` because it is not an active call assigned to the worker`;
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.HoldVoiceParticipantCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                reservationSid: reservation?.sid,
                targetParticipantSid: this.#targetParticipantSid
            });
            throwFlexSdkError(context)(
                ErrorCode.WorkerCannotHoldReservation,
                { severity: ErrorSeverity.Error },
                errorMsg
            );
        }

        return validReservation;
    }

    async #validateParticipant(
        holdContext: HoldVoiceParticipantContext,
        reservation: Reservation
    ): Promise<TaskParticipant> {
        const { cbm, logger, analytics, context, worker } = holdContext;
        const task = reservation.task;

        const participants = await handleGetParticipantsByTask(cbm, task, {
            actionName: "HoldVoiceParticipant",
            moduleName: "actions/Voice",
            logger,
            analytics,
            context,
            event: EVENTS.HoldVoiceParticipantCompleted,
            extra: {
                taskSid: this.#taskSid,
                reservationSid: reservation.sid,
                targetParticipantSid: this.#targetParticipantSid
            },
            worker
        });

        const participant = participants.find(
            (p) =>
                p.participantSid === this.#targetParticipantSid ||
                p.routingProperties?.workerSid === this.#targetParticipantSid
        );

        checkParticipantExists(participant, this.#targetParticipantSid, this.#taskSid, {
            actionName: "HoldVoiceParticipant",
            moduleName: "actions/Voice",
            logger,
            analytics,
            context,
            event: EVENTS.HoldVoiceParticipantCompleted,
            extra: {
                taskSid: this.#taskSid,
                reservationSid: reservation.sid,
                targetParticipantSid: this.#targetParticipantSid
            },
            worker
        });

        return participant!;
    }

    async #executeHold(
        holdContext: HoldVoiceParticipantContext,
        reservation: Reservation,
        participant: TaskParticipant
    ): Promise<void> {
        if (String(participant.type) === "external") {
            return this.#holdExternalParticipant(holdContext, reservation, participant);
        } else if (
            participant.routingProperties?.workerSid &&
            (String(participant.type) === "supervisor" || String(participant.type) === "agent")
        ) {
            return this.#holdWorkerParticipant(holdContext, reservation, participant);
        } else {
            return this.#holdGeneralParticipant(holdContext, reservation, participant);
        }
    }

    async #holdExternalParticipant(
        holdContext: HoldVoiceParticipantContext,
        reservation: Reservation,
        participant: TaskParticipant
    ): Promise<void> {
        const { cbm, analytics, worker, logger, context } = holdContext;
        const task = reservation.task;

        try {
            await cbm.holdParticipant(task, this.#targetParticipantSid);

            sendWorkerTrackingEvent(EVENTS.HoldVoiceParticipantCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                reservationSid: reservation.sid,
                targetParticipantSid: this.#targetParticipantSid,
                participantType: participant.type
            });

            return Promise.resolve();
        } catch (error: unknown) {
            const errorMsg = `HoldVoiceParticipant: Failed to hold external participant ${this.#targetParticipantSid}`;
            sendWorkerTrackingEvent(EVENTS.HoldVoiceParticipantCompleted, analytics, worker, {
                error: `${errorMsg} (${toCauseMessage(error)})`,
                taskSid: this.#taskSid,
                reservationSid: reservation.sid,
                targetParticipantSid: this.#targetParticipantSid,
                participantType: participant.type
            });
            logger.error(errorMsg, error);
            return throwFlexSdkError(context)(
                ErrorCode.FailedToHoldExternalParticipant,
                { severity: ErrorSeverity.Error },
                errorMsg,
                error
            );
        }
    }

    async #holdWorkerParticipant(
        holdContext: HoldVoiceParticipantContext,
        reservation: Reservation,
        participant: TaskParticipant
    ): Promise<void> {
        const { analytics, worker, logger, context, holdMusicUrl, holdMusicMethod } = holdContext;
        const task = reservation.task;

        try {
            const workerSid = participant.routingProperties?.workerSid;
            if (!workerSid) {
                throw new Error("Worker SID is required for worker participant hold");
            }

            await task.hold(workerSid, true, {
                ...(holdMusicUrl && { holdUrl: holdMusicUrl }),
                ...(holdMusicMethod && { holdMethod: holdMusicMethod })
            });

            sendWorkerTrackingEvent(EVENTS.HoldVoiceParticipantCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                reservationSid: reservation.sid,
                targetParticipantSid: this.#targetParticipantSid,
                participantSid: participant.participantSid,
                participantType: participant.type
            });

            return Promise.resolve();
        } catch (error: unknown) {
            const errorMsg = `HoldVoiceParticipant: Failed to hold worker participant ${this.#targetParticipantSid}`;
            sendWorkerTrackingEvent(EVENTS.HoldVoiceParticipantCompleted, analytics, worker, {
                error: `${errorMsg} (${toCauseMessage(error)})`,
                taskSid: this.#taskSid,
                reservationSid: reservation.sid,
                targetParticipantSid: this.#targetParticipantSid,
                participantSid: participant.participantSid,
                participantType: participant.type
            });
            logger.error(errorMsg, error);
            return throwFlexSdkError(context)(
                ErrorCode.FailedToHoldWorkerParticipant,
                { severity: ErrorSeverity.Error },
                errorMsg,
                error
            );
        }
    }

    async #holdGeneralParticipant(
        holdContext: HoldVoiceParticipantContext,
        reservation: Reservation,
        participant: TaskParticipant
    ): Promise<void> {
        const { analytics, worker, logger, context, holdMusicUrl, holdMusicMethod } = holdContext;
        const task = reservation.task;

        try {
            await task.updateParticipant({
                hold: true,
                ...(holdMusicUrl && { holdUrl: holdMusicUrl }),
                holdMethod: holdMusicMethod
            });

            sendWorkerTrackingEvent(EVENTS.HoldVoiceParticipantCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                reservationSid: reservation.sid,
                targetParticipantSid: this.#targetParticipantSid,
                participantType: participant?.type,
                participantSid: participant?.participantSid
            });

            return Promise.resolve();
        } catch (error: unknown) {
            const errorMsg = `HoldVoiceParticipant: Failed to hold participant ${this.#targetParticipantSid}`;
            sendWorkerTrackingEvent(EVENTS.HoldVoiceParticipantCompleted, analytics, worker, {
                error: `${errorMsg} (${toCauseMessage(error)})`,
                taskSid: this.#taskSid,
                reservationSid: reservation.sid,
                targetParticipantSid: this.#targetParticipantSid,
                participantType: participant?.type
            });
            logger.error(errorMsg, error);
            return throwFlexSdkError(context)(
                ErrorCode.FailedToHoldVoiceParticipant,
                { severity: ErrorSeverity.Error },
                errorMsg,
                error
            );
        }
    }
}
